import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * Multi-tenant workspace context.
 *
 * `currentWorkspace` is the active sandbox the user is viewing. When it
 * changes, dashboards re-fetch with `?workspace=<id>` so the data is sliced.
 * The user's available workspaces (and their access level) come from
 * GET /api/workspaces.
 */
const WorkspaceContext = createContext({
  workspaces: [],
  currentWorkspace: 'corporate',
  setCurrentWorkspace: () => {},
  loading: true,
  reload: async () => {},
  canWrite: false,
});

const STORAGE_KEY = 'canales_current_workspace';

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspaceState] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || 'corporate'; }
    catch { return 'corporate'; }
  });
  const [loading, setLoading] = useState(true);

  const setCurrentWorkspace = useCallback((ws) => {
    setCurrentWorkspaceState(ws);
    try { sessionStorage.setItem(STORAGE_KEY, ws); } catch { /* ignore */ }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/workspaces');
      const list = r.data?.workspaces || [];
      setWorkspaces(list);
      const savedOk = list.some((w) => w.id === currentWorkspace);
      if (!savedOk) {
        const def = r.data?.default || 'corporate';
        setCurrentWorkspaceState(def);
        try { sessionStorage.setItem(STORAGE_KEY, def); } catch { /* ignore */ }
      }
    } catch (e) {
      // 401 here is normal when not yet logged in. Reset to a safe default
      // so the next post-login reload populates the real list.
      setWorkspaces([]);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch workspaces whenever the auth state changes (login / logout).
  // This ensures the Topbar switcher is populated immediately after a
  // client-side navigation following a successful login (no hard reload
  // required).
  useEffect(() => {
    if (user) reload();
    else setWorkspaces([]);
  }, [user, reload]);

  const active = workspaces.find((w) => w.id === currentWorkspace);
  const canWrite = active ? active.access === 'rw' : false;

  return (
    <WorkspaceContext.Provider value={{ workspaces, currentWorkspace, setCurrentWorkspace, loading, reload, canWrite, active }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
